import React from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import AgencySwitcher from './AgencySwitcher';
import {
  Home,
  Users,
  DollarSign,
  ClipboardList,
  Shield,
  BarChart2,
  FileText,
  TestTube,
  UsersIcon,
  TrendingUp,
  Code,
  Target,
  BookOpen,
  SlidersHorizontal,
  Settings2,
  Inbox,
  KeyRound,
  FileCheck,
  FileOutput,
  Wrench,
  DatabaseBackup,
  BadgeCheck,
  Wallet,
  FileBarChart,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { visibleAdminSections } from '../constants/adminSections';
import { NAV_ITEMS } from '../constants/navItems';
import { FeatureFlag, PermissionSlug } from '../constants/featureFlags';
import { usePayrollAccess } from './payroll/usePayrollAccess';

type SidebarVariant = 'rogerdoc' | 'default';

interface SidebarPresentation {
  /** Render expanded regardless of the desktop collapse state (mobile drawer). */
  forceExpanded: boolean;
  /** Called after a menu item navigates (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const SidebarPresentationContext = React.createContext<SidebarPresentation>({
  forceExpanded: false,
});

interface SidebarMenuItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  /** Override the path used for active-state matching (defaults to `path`). */
  activePath?: string;
  variant?: SidebarVariant;
}

interface CollapsedTooltipProps {
  label: string;
  collapsed: boolean;
  children: React.ReactElement;
}

const CollapsedTooltip: React.FC<CollapsedTooltipProps> = ({
  label,
  collapsed,
  children,
}) => {
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side='right'>{label}</TooltipContent>
    </Tooltip>
  );
};

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  icon,
  label,
  path,
  activePath,
  variant = 'default',
}) => {
  const navigateTo = useNavigateWithNewTab();
  const location = useLocation();
  const { forceExpanded, onNavigate } = React.useContext(
    SidebarPresentationContext
  );
  const isCollapsed =
    useStore(state => state.isSidebarCollapsed) && !forceExpanded;
  const isActive = location.pathname.startsWith(activePath ?? path);

  const layoutClass = `flex items-center ${isCollapsed ? 'justify-center' : ''} w-full gap-2 rounded-sm px-2 py-2`;
  const stateClass =
    variant === 'rogerdoc'
      ? isActive
        ? 'bg-accent/50 text-primary font-medium'
        : 'hover:bg-accent/30 text-foreground'
      : `font-medium mb-1 ${isActive ? 'bg-accent text-primary' : 'hover:bg-accent text-foreground'}`;

  return (
    <CollapsedTooltip label={label} collapsed={isCollapsed}>
      <button
        onClick={e => {
          navigateTo(path)(e);
          onNavigate?.();
        }}
        className={`${layoutClass} ${stateClass}`}
      >
        {icon}
        {!isCollapsed && <span className='text-sm font-normal'>{label}</span>}
      </button>
    </CollapsedTooltip>
  );
};

interface SidebarProps {
  /** Mobile drawer rendering: always expanded, closes on navigation. */
  mobile?: boolean;
  /** Called after a menu item navigates (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ mobile = false, onNavigate }) => {
  const {
    user,
    hasPermission,
    hasAnyPermission,
    hasFeatureFlag,
    hasProduct,
    isSidebarCollapsed: isDesktopCollapsed,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
      hasProduct: state.hasProduct,
      isSidebarCollapsed: state.isSidebarCollapsed,
    }))
  );
  const isSidebarCollapsed = isDesktopCollapsed && !mobile;
  const presentation = React.useMemo(
    () => ({ forceExpanded: mobile, onNavigate }),
    [mobile, onNavigate]
  );
  const hasPayrollAccess = usePayrollAccess();
  const hasAdvancedReportingAccess =
    hasFeatureFlag(FeatureFlag.ADVANCED_REPORTING, 'workos') &&
    hasAnyPermission([
      PermissionSlug.REPORTS_VISITS,
      PermissionSlug.REPORTS_EPISODES,
      PermissionSlug.REPORTS_PATIENTS,
      PermissionSlug.REPORTS_CLAIMS,
      PermissionSlug.REPORTS_USER_LICENSES,
    ]);
  const hasReportsAccess =
    hasAdvancedReportingAccess ||
    hasAnyPermission([
      PermissionSlug.REPORTS_ACCESS,
      PermissionSlug.ROGER_EMR_REPORTS_ACCESS,
    ]);
  // We are using is_rogeremr_enabled to block access to the sidebar items that are only
  // available for agencies actually using RogerEMR.
  const rogerEmrEnabled = user?.agency?.is_rogeremr_enabled === true;

  return (
    <SidebarPresentationContext.Provider value={presentation}>
      <aside className='relative flex h-full min-h-0 w-full flex-col transition-all duration-300'>
        {/* Header */}
        {/* Matches the main Header's shadow; z-10 keeps it above the nav. */}
        <div className='relative z-10 flex h-16 shrink-0 items-center gap-2 border-b pl-3 pr-2 shadow-sm'>
          <div className='h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white text-2xl font-bold shrink-0'>
            R
          </div>
          {!isSidebarCollapsed && (
            <div className='min-w-0 flex-1'>
              <div className='font-semibold whitespace-nowrap'>Roger</div>
              <AgencySwitcher />
            </div>
          )}
        </div>

        {/* Menu */}
        <TooltipProvider delayDuration={300}>
          <nav className='min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-2 py-3'>
            {/* RogerDoc Dashboard Section */}
            {hasPermission(PermissionSlug.ROGERDOC_DASHBOARD_MANAGE) && (
              <>
                <SidebarMenuItem
                  icon={<BarChart2 className='h-4 w-4' />}
                  {...NAV_ITEMS.rogerDocOverview}
                  variant='rogerdoc'
                />
                <SidebarMenuItem
                  icon={<UsersIcon className='h-4 w-4' />}
                  {...NAV_ITEMS.rogerDocUsers}
                  variant='rogerdoc'
                />
                <SidebarMenuItem
                  icon={<TrendingUp className='h-4 w-4' />}
                  {...NAV_ITEMS.rogerDocUsage}
                  variant='rogerdoc'
                />
              </>
            )}

            {hasPermission(PermissionSlug.CUSTOMIZATIONS_ACCESS) && (
              <SidebarMenuItem
                icon={<SlidersHorizontal className='h-4 w-4' />}
                {...NAV_ITEMS.customizations}
                variant='rogerdoc'
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<FileText className='h-4 w-4' />}
                {...NAV_ITEMS.formsManager}
                variant='rogerdoc'
              />
            )}

            <SidebarMenuItem
              icon={<BookOpen className='h-4 w-4' />}
              {...NAV_ITEMS.resources}
              variant='rogerdoc'
            />

            {/* Divider between RogerDoc/Resources and EMR sections */}
            {!isSidebarCollapsed && (
              <div className='w-full h-px bg-border my-2' />
            )}

            {rogerEmrEnabled &&
              hasAnyPermission([
                PermissionSlug.HOTBOX_ACCESS,
                PermissionSlug.EMR_MANAGE,
                PermissionSlug.QA_ACCESS,
              ]) && (
                <SidebarMenuItem
                  icon={<Inbox className='h-4 w-4' />}
                  {...NAV_ITEMS.hotbox}
                />
              )}

            {rogerEmrEnabled &&
              hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS) && (
                <SidebarMenuItem
                  icon={<Home className='h-4 w-4' />}
                  {...NAV_ITEMS.dashboard}
                />
              )}

            {hasPermission(PermissionSlug.INTAKE_ACCESS) && (
              <SidebarMenuItem
                icon={<FileText className='h-4 w-4' />}
                {...NAV_ITEMS.intakeManager}
              />
            )}

            {hasPermission(PermissionSlug.CODING_ACCESS) && (
              <SidebarMenuItem
                icon={<Code className='h-4 w-4' />}
                {...NAV_ITEMS.medicalCoding}
              />
            )}

            {rogerEmrEnabled &&
              hasAnyPermission([
                PermissionSlug.PATIENT_MANAGER,
                PermissionSlug.EMR_MANAGE,
              ]) && (
                <SidebarMenuItem
                  icon={<Users className='h-4 w-4' />}
                  {...NAV_ITEMS.patientManager}
                />
              )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.ORDER_MANAGER) && (
              <SidebarMenuItem
                icon={<ClipboardList className='h-4 w-4' />}
                {...NAV_ITEMS.ordersManager}
              />
            )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.OASIS_MANAGER) && (
              <SidebarMenuItem
                icon={<FileOutput className='h-4 w-4' />}
                {...NAV_ITEMS.oasisManager}
              />
            )}

            {/* The fax product alone decides, so an intake-only agency has it. */}
            {hasProduct('fax') &&
              hasPermission(PermissionSlug.FAX_INBOX_MANAGE) && (
                <SidebarMenuItem
                  icon={<Inbox className='h-4 w-4' />}
                  {...NAV_ITEMS.faxInbox}
                />
              )}

            {rogerEmrEnabled && hasPermission(PermissionSlug.NOA_MANAGER) && (
              <SidebarMenuItem
                icon={<FileCheck className='h-4 w-4' />}
                {...NAV_ITEMS.noaManager}
              />
            )}

            {hasPermission(PermissionSlug.GOALS_ADMIN_TAB) && (
              <SidebarMenuItem
                icon={<Target className='h-4 w-4' />}
                {...NAV_ITEMS.worksheetGoals}
              />
            )}

            {rogerEmrEnabled && hasPayrollAccess && (
              <SidebarMenuItem
                icon={<Wallet className='h-4 w-4' />}
                {...NAV_ITEMS.payrollManager}
              />
            )}

            {hasAnyPermission([
              PermissionSlug.CLINICIAN_MANAGER,
              PermissionSlug.EMR_MANAGE,
            ]) && (
              <SidebarMenuItem
                icon={<Settings2 className='h-4 w-4' />}
                {...NAV_ITEMS.userManager}
              />
            )}

            {hasReportsAccess && (
              <SidebarMenuItem
                icon={<FileBarChart className='h-4 w-4' />}
                {...NAV_ITEMS.reports}
              />
            )}

            {rogerEmrEnabled &&
              hasPermission(PermissionSlug.BILLING_ACCESS) && (
                <SidebarMenuItem
                  icon={<DollarSign className='h-4 w-4' />}
                  {...NAV_ITEMS.billingManager}
                />
              )}

            {rogerEmrEnabled &&
              // Auth Manager not rolled out to end user yet, only super admin can see it
              hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
                <SidebarMenuItem
                  icon={<BadgeCheck className='h-4 w-4' />}
                  {...NAV_ITEMS.authManager}
                  activePath='/auth'
                />
              )}

            {visibleAdminSections(
              hasAnyPermission,
              rogerEmrEnabled,
              hasFeatureFlag,
              user?.is_roger_employee === true
            ).length > 0 && (
              <SidebarMenuItem
                icon={<Shield className='h-4 w-4' />}
                {...NAV_ITEMS.admin}
              />
            )}

            {hasAnyPermission([PermissionSlug.ADMIN_SUPER_USER]) && (
              <SidebarMenuItem
                icon={<KeyRound className='h-4 w-4' />}
                {...NAV_ITEMS.rolesManager}
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<Shield className='h-4 w-4' />}
                {...NAV_ITEMS.internalAdmin}
              />
            )}

            {hasPermission(PermissionSlug.IMPORTER_ACCESS) && (
              <SidebarMenuItem
                icon={<DatabaseBackup className='h-4 w-4' />}
                {...NAV_ITEMS.importer}
              />
            )}

            {hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
              <SidebarMenuItem
                icon={<TestTube className='h-4 w-4' />}
                {...NAV_ITEMS.testing}
              />
            )}

            {user?.agency?.system_name === 'Strawhat' &&
              hasPermission(PermissionSlug.ADMIN_SUPER_USER) && (
                <SidebarMenuItem
                  icon={<Wrench className='h-4 w-4' />}
                  {...NAV_ITEMS.internalTools}
                />
              )}
          </nav>
        </TooltipProvider>
      </aside>
    </SidebarPresentationContext.Provider>
  );
};

export default Sidebar;
